import type { DetectedVehicle, ValidationError } from "../types/batchImport";

export const BATCH_IMPORT_REQUIRED_FIELDS = [
  "brand",
  "model",
  "mileageFrom",
  "powerFrom",
  "registrationFrom",
  "fuel",
  "transmission",
  "vehicleType",
] as const;

type RequiredBatchImportField = (typeof BATCH_IMPORT_REQUIRED_FIELDS)[number];

const REQUIRED_FIELD_LABELS: Record<RequiredBatchImportField, string> = {
  brand: "Marke",
  model: "Modell",
  mileageFrom: "Kilometerstand",
  powerFrom: "Leistung (kW)",
  registrationFrom: "Erstzulassung",
  fuel: "Kraftstoff",
  transmission: "Getriebe",
  vehicleType: "Bauart",
};

const hasRequiredValue = (
  vehicle: DetectedVehicle,
  field: RequiredBatchImportField,
): boolean => {
  if (field === "brand") return Number.isInteger(vehicle.brandId) && vehicle.brandId! > 0;
  if (field === "model") return Number.isInteger(vehicle.modelId) && vehicle.modelId! > 0;
  if (field === "mileageFrom") {
    return Number.isFinite(vehicle.mileageFrom) && vehicle.mileageFrom! >= 0;
  }
  if (field === "powerFrom") {
    return Number.isFinite(vehicle.powerFrom) && vehicle.powerFrom! > 0;
  }

  const value = vehicle[field];
  return typeof value === "string" && value.trim().length > 0;
};

export function getBatchImportValidationErrors(
  vehicle: DetectedVehicle,
): ValidationError[] {
  const errors = vehicle.errors.filter((error) => error.severity === "error");
  const seenFields = new Set(errors.map((error) => error.field));

  for (const field of BATCH_IMPORT_REQUIRED_FIELDS) {
    if (!hasRequiredValue(vehicle, field) && !seenFields.has(field)) {
      errors.push({
        row: vehicle.rowNumber,
        field,
        value: undefined,
        message: `${REQUIRED_FIELD_LABELS[field]} ist ein Pflichtfeld`,
        severity: "error",
      });
    }
  }

  return errors;
}

export function isBatchImportVehicleValid(vehicle: DetectedVehicle): boolean {
  return getBatchImportValidationErrors(vehicle).length === 0;
}

export function calculateBatchImportStats(vehicles: DetectedVehicle[]) {
  const errorRows = vehicles.filter(
    (vehicle) => !isBatchImportVehicleValid(vehicle),
  ).length;
  const warningRows = vehicles.filter(
    (vehicle) => vehicle.warnings.length > 0,
  ).length;

  return {
    errorRows,
    warningRows,
    validRows: vehicles.length - errorRows,
  };
}
